import React, { useCallback, useEffect, useState, useRef } from "react";
import { Box, Calendar, DataTable, Text, TextInput } from "grommet";
import { observer } from "mobx-react";
import { useStore } from "../../../../../../Hooks/useStore";
import { Mixpanel } from "../../../../../../util/Helper";
import CommonButton from "../../../common/Button/CommonButton";
import CommonSearchInput from "../../../common/SearchInput/CommonSearchInput";
import YearSelection from "../../EMS/common/YearSelection/YearSelection";
import "./UserActivity.css";
import calendar from "../../../../../../assets/edit_calendar.svg";
import MedicalEmergencyService from "../../../../../../services/medical-emergency/medical-emergency.service";
import searchIcon from "../../../../../../assets/iconamoon_search-light.svg";
import UserActivityManager from "../../../../../../util/report/UserActivity/UserActivityManager";
import Pagination from "../../../common/Pagination/Pagination";
import Utility from "../../../../../../util/api/Utility";
import noResult from "../../../../../../assets/no-result.png";
import { useOutsideClick } from './useOutsideClick';



const UsernameComponent = ({ item }) => {
  const [username, setUsername] = useState('');

  useEffect(() => {
    const fetchUsername = async () => {
      if (item.name !== undefined || item.name !== "") {
        setUsername(item.name);
      } else if (item.$user_id) {
        try {
          const response = UserActivityManager.getUserNameByCognitoId(item.$user_id);
          setUsername(response.userName);
        } catch (error) {
          console.log("error in getting username by cognito id", error);
        }
      } else {
        return setUsername("");
      }
    };
    fetchUsername();
  }, [item]);

  return (
    <div>
      {username}
    </div>
  );
};

const UserActivity = observer(() => {
  const { rootStore } = useStore();
  const [originalList, setOriginalList] = useState([]);
  const [list, setList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchString, setSearchString] = useState("");
  const [sortedList, setSortedList] = useState([]);
  const totalItems = list.length;
  const totalPage = Math.ceil(totalItems / itemsPerPage);

  const [showDateRange, setShowDateRange] = useState(false);
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);
  const [limit, setLimit] = useState(100);
  const [hasMore, setHasMore] = useState(false);
  useEffect(() => {
    // setLimit(100);
    // setHasMore(true);
    getHistory();
  }, [rootStore.userActivityStore.getSelectedDate, limit]);

  useEffect(() => {
    setOriginalList(rootStore.userActivityStore.getUserActivityHistory);
  }, [rootStore.userActivityStore.getUserActivityHistory]);

  useEffect(() => {
    if (searchString === "") {
      setList(originalList);
    }
  }, [searchString, originalList]);

  const onSearchStrChange = useCallback((value) => {
    setSearchString(value);
  }, []);

  const onSearch = useCallback(() => {
    setLimit(100);
    setHasMore(true);
    const filteredUserActivity = originalList.filter((item) => {
      return (
        (item.name &&
          item.name.toLowerCase().includes(searchString.toLowerCase())) ||
        (item.event &&
          item.event.toLowerCase().includes(searchString.toLowerCase())) ||
        (item.$device_id &&
          item.$device_id.toLowerCase().includes(searchString.toLowerCase())) ||
        (item.$city &&
          item.$city.toLowerCase().includes(searchString.toLowerCase()))
      );
    });

    setList(filteredUserActivity);
    setCurrentPage(1);
  }, [searchString, originalList]);

  const onYearSelected = useCallback((year) => {
    let uInfos = localStorage.getItem("userinfo");
    if (uInfos) {
      const userinformation = JSON.parse(uInfos);
      Mixpanel.track("web_medical_history_year_select", {
        name: userinformation.username,
      });
    }
    rootStore.userActivityStore.setYearSelected(year);
    rootStore.userActivityStore.handleYearSelectionShow();
  }, []);

  const onMonthSelected = useCallback((month) => {
    let uInfos = localStorage.getItem("userinfo");
    if (uInfos) {
      const userinformation = JSON.parse(uInfos);
      Mixpanel.track("web_medical_history_month_select", {
        name: userinformation.username,
      });
    }
    rootStore.userActivityStore.setMonthSelected(month);
    rootStore.userActivityStore.handleYearSelectionShow();
  }, []);

  const onTodayClick = () => {
    rootStore.userActivityStore.handleCalendarShow();
  };

  useEffect(() => {
    const date = new Date(
      Date.parse(rootStore.userActivityStore.getSelectedDate)
    );
    date.setFullYear(
      Number.parseInt(rootStore.userActivityStore.getSelectedYear)
    );
    date.setMonth(
      Number.parseInt(rootStore.userActivityStore.getSelectedMonth)
    );
    rootStore.userActivityStore.setSelectedDate(date.toISOString());
  }, [
    rootStore.userActivityStore.getSelectedYear,
    rootStore.userActivityStore.getSelectedMonth,
  ]);

  const getHistory = async (searchStr = "") => {
    rootStore.loadingStore.setShowLoader(true);
    const fromDate = rootStore.userActivityStore.getFromDate.split("T")[0];
    const toDate = rootStore.userActivityStore.getToDate.split("T")[0];
  
    try {
      const projectId = process.env.REACT_APP_ENV === "PROD" ? 3370392 : 3210213;
  
      const activityHistory = await MedicalEmergencyService.getUserActivityHistory(
        fromDate,
        toDate,
        projectId,
        limit
      );
  
      let data = activityHistory.map((item) => ({
        event: item.event,
        ...item.properties,
      }));
  
      // Sort by time descending (latest first)
      data.sort((a, b) => b.time - a.time);
  
      // Apply search filter if needed
      if (searchStr) {
        data = data.filter((item) => {
          return (
            (item.name &&
              item.name.toLowerCase().includes(searchStr.toLowerCase())) ||
            (item.event &&
              item.event.toLowerCase().includes(searchStr.toLowerCase())) ||
            (item.$device_id &&
              item.$device_id.toLowerCase().includes(searchStr.toLowerCase())) ||
            (item.$city &&
              item.$city.toLowerCase().includes(searchStr.toLowerCase()))
          );
        });
      }
  
      setOriginalList(data);
      setList(data);
      setHasMore(data.length >= limit);
    } catch (error) {
      console.log("Failed to fetch history:", error);
    } finally {
      rootStore.loadingStore.setShowLoader(false);
    }
  };
  

  const handleLoadMore = () => {
    const newLimit = limit + 100;
    setLimit(newLimit);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // useEffect(() => {
  //   const startIndex = (currentPage - 1) * itemsPerPage;
  //   const endIndex = startIndex + itemsPerPage;
  //   setSortedList(list.slice(startIndex, endIndex));
  // }, [currentPage, itemsPerPage, list, rootStore.userActivityStore.getUserActivityHistory]);

  useEffect(() => {
    setSortedList(list); // Show entire list loaded so far
  }, [list]);

  const totalPages = Math.ceil(list.length / itemsPerPage);
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      onSearch(searchString); // Trigger search when Enter key is pressed
    }
  };
  const disabledDays = ({ date }) => date > new Date();

  const handleTextClick = () => {
    rootStore.userActivityStore.handleYearSelectionShow();
  }

  useEffect(() => {
    const textElement = document.querySelector('.activity-calendar h3')
    if (textElement) {
      textElement.addEventListener('click', handleTextClick);
    }

    return () => {
      const textElement = document.querySelector('.activity-calendar h3');
      if (textElement) {
        textElement.removeEventListener('click', handleTextClick);
      }
    }
  }, [rootStore.userActivityStore.getCalendarShow]);

  const getFormatedDate = (givenDate) => {
    let localDateTime = Utility.convertUTCToLocal(givenDate);
    let formattedDate = Utility.getFormatedDate(localDateTime);
    return formattedDate;
  };


  // Refs for the calendar containers
  const fromCalendarRef = useRef(null);
  const toCalendarRef = useRef(null);
  const fromTriggerRef = useRef(null);
  const toTriggerRef = useRef(null);

  // Close from calendar when clicking outside
  useOutsideClick(fromCalendarRef, () => {
    if (showFromCalendar) {
      setShowFromCalendar(false);
    }
  });

  // Close to calendar when clicking outside
  useOutsideClick(toCalendarRef, () => {
    if (showToCalendar) {
      setShowToCalendar(false);
    }
  });

  return (
    <Box className="user-activity-container">
      <div className="user-activity-search">
        <div className="user-activity-blue">
          <div className="search">
            <CommonSearchInput onkeydown={handleKeyDown} onchange={(e) => onSearchStrChange(e.target.value)} />
            <CommonButton
              title="Search"
              padding="10px 24px 10px 28px"
              color="#fff"
              bgColor="#357EFF"
              radius={10}
              border="1px solid #357EFF"
              lineheight="14px"
              ml={5}
              onclick={onSearch}
            />
          </div>
          {/* <div className="filter-content">
            <div className="calendar">
              <div onClick={onTodayClick}>
                <span>Today</span>
                <img src={calendar} alt="calendar" />
              </div>
              {rootStore.userActivityStore.getCalendarShow && (
                <div className="calender-component">
                  {rootStore.userActivityStore.getYearSelection && (
                    <YearSelection
                      onYearSelected={onYearSelected}
                      onMonthSelected={onMonthSelected}
                      selectedYear={rootStore.emsStore.getSelectedYear}
                    />
                  )}
                  <Calendar
                    className="activity-calendar"
                    size="medium"
                    date={rootStore.userActivityStore.getSelectedDate}
                    onSelect={(date) => {

                      let username;
       
                      // get user info
                      let userInfo = localStorage.getItem("userinfo");
                      console.log("userInfo in logoutSession", userInfo);
                      if(userInfo) {
                          username = JSON.parse(userInfo)?.username;
                      }
                      Mixpanel.track("web_user-activity_date_select",{name:username});
                      rootStore.userActivityStore.handleCalendarShow();
                      const selectedDate = new Date(date);
                      const today = new Date();
                      if (selectedDate > today) {
                        rootStore.notificationToastStore.showNotification("The selected date cannot be in the future. Please choose a valid date.",true, 3000);
                      } else {
                        rootStore.userActivityStore.setSelectedDate(
                          date.toString()
                        );
                      }
                    }}
                    animate={true}
                    fill={true}
                    daysOfWeek={true}
                    // Custom render function to style dates
                    dayProps={({ date, ...rest }) => {
                      const isPast = date < new Date();

                      return {
                        style: {
                          color: isPast ? "#357EFF" : "#CCCCCC",
                          cursor: "pointer",
                        },
                        ...rest,
                      };
                    }}
                  />
                </div>
              )}
            </div>
          </div> */}

          {/* <div onClick={() => setShowDateRange((prev) => !prev)} style={{ cursor: "pointer", marginTop: "10px", display: 'flex', alignItems: 'center', gap: '6px' }}>
    <span>Date Range</span>
    <img src={calendar} alt="calendar" />
  </div> */}

          {/* {showDateRange && ( */}
          <div className="filter-content">
            <div className="calendar-range">
              {/* From Date Section */}
              <div className="calendar-group">
                <div ref={fromTriggerRef} className="calendar" onClick={() => setShowFromCalendar(!showFromCalendar)}>
                  <span>From: {getFormatedDate(rootStore.userActivityStore.getFromDate)}</span>
                  <img src={calendar} alt="calendar" />
                </div>
                {showFromCalendar && (
                  <div ref={fromCalendarRef} className="calender-component">
                    <Calendar
                      className="activity-calendar"
                      size="medium"
                      date={rootStore.userActivityStore.getFromDate}
                      onSelect={(date) => {
                        const selectedDate = new Date(date);
                        const toDate = new Date(rootStore.userActivityStore.getToDate);

                        if (selectedDate > toDate) {
                          rootStore.notificationToastStore.showNotification(
                            "From date cannot be after To date.",
                            true,
                            3000
                          );
                          return;
                        }

                        rootStore.userActivityStore.setFromDate(date);
                        setShowFromCalendar(false);

                        // Only call API if both dates are selected
                        // if (rootStore.userActivityStore.getFromDate && rootStore.userActivityStore.getToDate) {
                        //   getHistory();
                        // }
                      }}
                      animate={true}
                      fill={true}
                      daysOfWeek={true}
                      bounds={['2000-01-01', new Date().toISOString()]}
                      dayProps={({ date, ...rest }) => {
                        const isPast = date < new Date();
                        return {
                          style: {
                            color: isPast ? "#357EFF" : "#CCCCCC",
                            cursor: "pointer",
                          },
                          ...rest,
                        };
                      }}
                    />
                  </div>
                )}
              </div>

              {/* To Date Section */}
              <div className="calendar-group">
                <div ref={toTriggerRef} className="calendar" onClick={() => setShowToCalendar(!showToCalendar)}>
                  <span>To: {getFormatedDate(rootStore.userActivityStore.getToDate)}</span>
                  <img src={calendar} alt="calendar" />
                </div>
                {showToCalendar && (
                  <div ref={toCalendarRef} className="calender-component">
                    <Calendar
                      className="activity-calendar"
                      size="medium"
                      date={rootStore.userActivityStore.getToDate}
                      onSelect={(date) => {
                        const selectedDate = new Date(date);
                        const fromDate = new Date(rootStore.userActivityStore.getFromDate);

                        if (selectedDate < fromDate) {
                          rootStore.notificationToastStore.showNotification(
                            "To date cannot be before From date.",
                            true,
                            3000
                          );
                          return;
                        }

                        rootStore.userActivityStore.setToDate(date);
                        setShowToCalendar(false);

                        // Only call API if both dates are selected
                        // if (rootStore.userActivityStore.getFromDate && rootStore.userActivityStore.getToDate) {
                        //   getHistory();
                        // }
                      }}
                      animate={true}
                      fill={true}
                      daysOfWeek={true}
                      bounds={['2000-01-01', new Date().toISOString()]}
                      dayProps={({ date, ...rest }) => {
                        const isPast = date < new Date();
                        return {
                          style: {
                            color: isPast ? "#357EFF" : "#CCCCCC",
                            cursor: "pointer",
                          },
                          ...rest,
                        };
                      }}
                    />
                  </div>
                )}
              </div>
              <CommonButton
                title="Search"
                padding="10px 24px 10px 28px"
                color="#fff"
                bgColor="#357EFF"
                radius={10}
                border="1px solid #357EFF"
                lineheight="14px"
                ml={5}
                onclick={() => {
                  setLimit(100);
                  setHasMore(true);
                  setCurrentPage(1);
                  getHistory(searchString); // Pass current search value if any
                }}
              />
            </div>

          </div>
          {/* )} */}


        </div>
      </div>
      <div className="over">
        <div className="table">
          {sortedList.length === 0 ? (
            <Box className="no-result-img">
              <img src={noResult} alt="no Data found" />
              <p>No Data found.</p>
            </Box>
          ) : (
            <DataTable
              className="user-activity-datatable"
              data={sortedList}
              resizeable
              step={itemsPerPage}
              paginate={false}
              columns={[
                {
                  property: "Username",
                  header: <Text style={{textAlign:"center"}}>Username</Text>,
                  primary: true,
                  render: (item) => {
                    return (
                      <Text style={{textAlign:"center"}}>
                        {<UsernameComponent item={item} />}
                      </Text>
                    );
                  }
                },
                {
                  property: "Event Name",
                  header: <Text style={{textAlign:"center"}}>Event Name</Text>,
                  primary: true,
                  render: (item) => <Text style={{textAlign:"center"}}>{item.event}</Text>,
                },
                {
                  property: "Time",
                  header: <Text style={{textAlign:"center"}}>Time</Text>,
                  primary: true,
                  render: (item) => {
                    const formattedDateTime = new Date(item.time * 1000);
                    const formattedDate = getFormatedDate(formattedDateTime);
                    return <Text style={{textAlign:"center"}}>{formattedDate}</Text>;
                  },
                },
                {
                  property: "Device",
                  header: <Text style={{textAlign:"center"}}>Device</Text>,
                  primary: true,
                  render: (item) => <Text style={{textAlign:"center"}}>{item.$device_id}</Text>,
                },
                {
                  property: "City",
                  header: <Text style={{textAlign:"center"}}>City</Text>,
                  primary: true,
                  render: (item) => <Text style={{textAlign:"center"}}>{item.$city}</Text>,
                },
              ]}
            />
          )}
        </div>

      </div>
      {/* <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={list.length}
        onPageChange={handlePageChange}
      /> */}
      {hasMore && (
        <Box align="center" margin={{ top: "medium" }}>
          <CommonButton
            title="Load More"
            padding="10px 24px"
            color="#fff"
            bgColor="#357EFF"
            radius={10}
            border="1px solid #357EFF"
            onclick={handleLoadMore}
          />
        </Box>
      )}
    </Box>
  );
});

export default UserActivity;
